import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  NUMERIC_FILTER_MODE_OPTIONS,
  parseNumericInput,
} from '../table-helpers';
import type { NumericFilterMode, NumericFilterValue } from '../table-types';
import FilterDropdown from './filter-dropdown';

const FILTER_TEXT_SIZE = '11px';

type FilterOption = {
  id: string;
  label: string;
};

type NumericColumnFilterProps = {
  valueOptions: FilterOption[];
  value: NumericFilterValue | undefined;
  onChange: (value: NumericFilterValue | undefined) => void;
  isPercentage?: boolean;
};

const NumericColumnFilter = ({
  valueOptions,
  value,
  onChange,
  isPercentage = false,
}: NumericColumnFilterProps) => {
  const [mode, setMode] = useState<NumericFilterMode>(value?.mode ?? 'values');
  const modeChangedInternally = useRef(false);

  const [minInput, setMinInput] = useState<string>(
    value?.min !== undefined ? value.min.toString() : '',
  );
  const [maxInput, setMaxInput] = useState<string>(
    value?.max !== undefined ? value.max.toString() : '',
  );
  const [singleInput, setSingleInput] = useState<string>(
    value?.value !== undefined ? value.value.toString() : '',
  );

  useEffect(() => {
    if (!modeChangedInternally.current) {
      setMode(value?.mode ?? 'values');
    }
    modeChangedInternally.current = false;
    setMinInput(value?.min !== undefined ? value.min.toString() : '');
    setMaxInput(value?.max !== undefined ? value.max.toString() : '');
    setSingleInput(value?.value !== undefined ? value.value.toString() : '');
  }, [value]);

  const currentModeOption = useMemo(
    () => NUMERIC_FILTER_MODE_OPTIONS.find((m) => m.id === mode),
    [mode],
  );

  const handleModeChange = useCallback(
    (newMode: NumericFilterMode) => {
      modeChangedInternally.current = true;
      setMode(newMode);
      setMinInput('');
      setMaxInput('');
      setSingleInput('');
      onChange(undefined);
    },
    [onChange],
  );

  const handleValuesChange = useCallback(
    (selectedIds: string[]) => {
      if (selectedIds.length > 0) {
        onChange({
          mode: 'values',
          selectedValues: selectedIds,
        });
      } else {
        onChange(undefined);
      }
    },
    [onChange],
  );

  const handleRangeChange = useCallback(() => {
    const min = parseNumericInput(minInput);
    const max = parseNumericInput(maxInput);

    if (min !== undefined || max !== undefined) {
      onChange({ mode, min, max });
    } else {
      onChange(undefined);
    }
  }, [mode, minInput, maxInput, onChange]);

  const handleSingleValueChange = useCallback(() => {
    const val = parseNumericInput(singleInput);

    if (val !== undefined) {
      onChange({ mode, value: val });
    } else {
      onChange(undefined);
    }
  }, [mode, singleInput, onChange]);

  const getPlaceholder = (field: 'min' | 'max' | 'value') => {
    if (isPercentage) {
      return field === 'value'
        ? 'e.g. 25%'
        : field === 'min'
          ? 'Min %'
          : 'Max %';
    }
    return field === 'value' ? 'Value' : field === 'min' ? 'Min' : 'Max';
  };

  const isBetweenMode =
    mode === 'betweenInclusive' || mode === 'betweenExclusive';

  const isSingleValueMode = [
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
  ].includes(mode);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          width: '100%',
        }}
      >
        <Autocomplete
          size="small"
          options={[...NUMERIC_FILTER_MODE_OPTIONS]}
          value={currentModeOption}
          onChange={(_, newValue) => {
            if (newValue) {
              handleModeChange(newValue.id as NumericFilterMode);
            }
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              {...params}
              variant="standard"
              size="small"
              sx={{ fontSize: FILTER_TEXT_SIZE }}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  sx: { fontSize: FILTER_TEXT_SIZE },
                },
              }}
            />
          )}
          disableClearable
          forcePopupIcon={false}
          sx={{ flex: 1, minWidth: 80 }}
          slotProps={{
            listbox: {
              sx: { fontSize: FILTER_TEXT_SIZE, width: 'auto', minWidth: 150 },
            },
            popper: {
              sx: { width: 'auto !important', minWidth: 150 },
              placement: 'bottom-start',
            },
          }}
        />
      </div>

      {mode === 'values' && (
        <FilterDropdown
          options={valueOptions}
          onChange={handleValuesChange}
          value={value?.selectedValues}
        />
      )}

      {isBetweenMode && (
        <div style={{ display: 'flex', gap: '4px' }}>
          <TextField
            size="small"
            variant="standard"
            placeholder={getPlaceholder('min')}
            value={minInput}
            onChange={(e) => setMinInput(e.target.value)}
            onBlur={handleRangeChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                handleRangeChange();
              }
            }}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                sx: { fontSize: FILTER_TEXT_SIZE },
              },
            }}
          />
          <TextField
            size="small"
            variant="standard"
            placeholder={getPlaceholder('max')}
            value={maxInput}
            onChange={(e) => setMaxInput(e.target.value)}
            onBlur={handleRangeChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                handleRangeChange();
              }
            }}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                sx: { fontSize: FILTER_TEXT_SIZE },
              },
            }}
          />
        </div>
      )}

      {isSingleValueMode && (
        <TextField
          size="small"
          variant="standard"
          placeholder={getPlaceholder('value')}
          value={singleInput}
          onChange={(e) => setSingleInput(e.target.value)}
          onBlur={handleSingleValueChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              handleSingleValueChange();
            }
          }}
          slotProps={{
            input: {
              sx: { fontSize: FILTER_TEXT_SIZE },
            },
          }}
        />
      )}
    </div>
  );
};

export default NumericColumnFilter;
